/* eslint-disable max-len */
import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { _, _lazy } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

@connect((state, props) => {
    return {
        enableNitogrenRecommendation: getAppInstance(state)?.enableNitogrenRecommendation,
    };
})
@autobind
class Fertilization extends Component {
    /* Note: If the multiplier we're using is 'environment' then the recommendation is the same for all products */

    static label = _lazy('Fertilización');
    static icon = 'fertilization';

    static contextTypes = {
        units: PropTypes.object,
        history: PropTypes.object,
    };

    static propTypes = {
        text: PropTypes.string,
        notes: PropTypes.arrayOf(PropTypes.string),
        multiplier: PropTypes.string.isRequired,
    };

    state = {
        selectedProductRecommendation: this.props.productRecommendations[0],
    };

    override componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<{}>, snapshot?: any): void {
        if (prevProps.productRecommendations !== this.props.productRecommendations) {
            this.setState({
                selectedProductRecommendation: this.props.productRecommendations[0],
            });
        }
    }

    getMultiplierHeader() {
        return {
            environment: _('Ambiente'),
            estimatedYield: _('Rendimiento'),
        }[this.props.multiplier];
    }

    getNutrients() {
        return ['n', 'p', 'k', 's'].map((nutrient) => ({
            nutrient,
            header: `${nutrient?.[0]?.toUpperCase()}${nutrient.slice(1)}`,
        }));
    }

    getNutrientProps(data, nutrient) {
        const value = data[nutrient];

        if (nutrient === 'n') {
            if (value.recommended) {
                const { theme } = this.props.productRecommendations?.[0]?.profile.product.brand;
                const bgRecommended = classNames(styles.bgRecommended, styles[`theme-${theme}`]);

                return {
                    value:
                        typeof value.recommended === 'number' && !Number.isNaN(value.recommended)
                            ? value.recommended?.toFixed(2)
                            : '-',
                    className: bgRecommended,
                };
            }
            return {
                value:
                    typeof value.common === 'number' && !Number.isNaN(value.common)
                        ? `${value.common?.toFixed(2)} kg/ha`
                        : '-',
                className: typeof value.common === 'number' && !Number.isNaN(value.common) && styles.notAvailable,
            };
        }

        return {
            value: typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(2) : '-',
            className: typeof value === 'number' && !Number.isNaN(value) && styles.notAvailable,
        };
    }

    handleProductRecommendationSelection(selectedProductRecommendation) {
        this.setState({ selectedProductRecommendation });
    }

    renderMobile() {
        const { units } = this.context;
        const {
            cropConfig: { crop, environment },
            productRecommendations,
            multiplier,
        } = this.props;

        const { fertilization, estimatedYield } = this.state.selectedProductRecommendation;
        const productRecommendationOptions =
            multiplier !== 'environment' &&
            productRecommendations.map((productRecommendation) => ({
                value: productRecommendation,
                label: productRecommendation.profile.label,
            }));

        // Build columns
        const rows = [];
        const values = this.getNutrients().map(({ header, nutrient }) => ({
            header,
            props: this.getNutrientProps(fertilization, nutrient),
        }));

        const columns = 2;
        while (values.length) {
            rows.push(values.splice(0, columns));
        }

        return (
            <div className={styles.rootMobile}>
                <div>
                    {productRecommendationOptions && (
                        <Select
                            appearance='fill'
                            color={settings.app.theme}
                            options={productRecommendationOptions}
                            value={this.state.selectedProductRecommendation}
                            clearable={false}
                            placeholder={_('Producto')}
                            persistPlaceholder
                            onChange={this.handleProductRecommendationSelection}
                        />
                    )}
                </div>
                <div className={styles.multiplier}>
                    <span className={styles.label}>{this.getMultiplierHeader()}:</span>
                    {multiplier === 'environment'
                        ? units.toYield(environment, crop).toString()
                        : !isNaN(estimatedYield.forRecommendedHarvestDensity)
                          ? units.toYield(estimatedYield.forRecommendedHarvestDensity, crop).toString()
                          : '-'}
                </div>
                <table className={styles.table}>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.map(({ header }) => header).join('-')}>
                                {row.map(({ header, props }) => (
                                    <Fragment key={header}>
                                        <td className={props.className}>
                                            <span className={styles.label}>{header}:</span>
                                            {props.value}
                                        </td>
                                        {row.length < columns && <td />}
                                    </Fragment>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderDesktop() {
        const { units } = this.context;
        const {
            cropConfig: { crop, environment },
            productRecommendations,
            multiplier,
        } = this.props;
        const nutrients = this.getNutrients();
        return (
            <table className={classNames(styles.rootDesktop, styles.table)}>
                <thead>
                    <tr>
                        {multiplier !== 'environment' && <th />}
                        <th className={styles.multiplier}>{this.getMultiplierHeader()}</th>
                        {nutrients.map(({ header }) => (
                            <th key={header} className={styles.nutrientCell}>
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {multiplier === 'environment' ? (
                        <tr>
                            <td className={styles.multiplierValue}>{units.toYield(environment, crop).toString()}</td>
                            {nutrients.map(({ header, nutrient }) => {
                                const { profile, fertilization } = this.state.selectedProductRecommendation;
                                const props = this.getNutrientProps(fertilization, nutrient);
                                return (
                                    <td
                                        key={`${profile.product.id}-${header}`}
                                        className={classNames(styles.nutrientCell, props.className)}
                                    >
                                        {props.value}
                                    </td>
                                );
                            })}
                        </tr>
                    ) : (
                        productRecommendations.map(({ profile, fertilization, estimatedYield }) => (
                            <tr key={`${profile.product.id}`}>
                                <td>{profile.label}</td>
                                <td className={styles.multiplier}>
                                    {!isNaN(estimatedYield.forRecommendedHarvestDensity)
                                        ? units.toYield(estimatedYield.forRecommendedHarvestDensity, crop).toString()
                                        : '-'}
                                </td>
                                {nutrients.map(({ header, nutrient }) => {
                                    const props = this.getNutrientProps(fertilization, nutrient);
                                    return (
                                        <td
                                            key={`${profile.product.id}-${header}`}
                                            className={classNames(styles.nutrientCell, props.className)}
                                        >
                                            {props.value}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        );
    }

    render() {
        const field =
            typeof this.props.recommendation.field === 'object' ? this.props.recommendation.field : this.props.field;
        return (
            <div className={styles.root}>
                {this.props.text && <div className={styles.text}>{this.props.text}</div>}
                {this.renderMobile()}
                {this.renderDesktop()}
                <div className={styles.disclaimer}>
                    {_(
                        'Requerimiento de nutrientes expresado en kg/ha considerando un {{ grainMoisture }}% de humedad a cosecha.',
                        {
                            grainMoisture: this.state.selectedProductRecommendation.fertilization.grainMoisture,
                        }
                    )}
                </div>
                {!!this.props.notes.length && (
                    <Fragment>
                        {this.props.notes.map((text) => (
                            <Alert key={text} severity='blue'>
                                {text}
                            </Alert>
                        ))}
                    </Fragment>
                )}

                {this.props.enableNitogrenRecommendation &&
                    (field.geometry.length > 1 ? (
                        <Stack width={'fit-content'}>
                            <Button
                                variant='contained'
                                onClick={() => {
                                    mpTrackEvent('N-Smart Recommendation began', {
                                        entry_source: 'crop-recommendation-detail',
                                    });
                                    this.context.history(
                                        reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                                            params: {
                                                fieldId: field.id,
                                            },
                                        })
                                    );
                                }}
                            >
                                {_('Solicitar recomendación de Nitrógeno Smart')}
                            </Button>
                        </Stack>
                    ) : (
                        <Stack width={'fit-content'}>
                            <Button
                                variant='contained'
                                onClick={() => {
                                    this.context.history(reverse('nitrogenRecommendation.NitrogenRecommendationList'));
                                }}
                            >
                                {_('Solicitar recomendación de Nitrógeno Smart')}
                            </Button>
                        </Stack>
                    ))}
            </div>
        );
    }
}

const FertilizationWrapper = (props: unknown) => {
    const fieldsQuery = useFieldById(
        {
            id: props.recommendation.field,
        },
        {
            enabled: typeof props.recommendation.field !== 'object',
        }
    );
    if (fieldsQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center' my={2}>
                <Spinner />
            </Stack>
        );
    return <Fertilization {...props} field={fieldsQuery.data} />;
};

@autobind
export class CornFertilization extends Component {
    static label = Fertilization.label;
    static icon = Fertilization.icon;

    getNotes() {
        const { productRecommendations } = this.props;
        const notes = [
            _(
                'Recordá que fertilizar es aplicar nutrientes en forma balanceada y precisa para el desarrollo sustentable.'
            ),
        ];

        if (
            productRecommendations.some(
                ({
                    fertilization: {
                        n: { recommended },
                    },
                }) => recommended !== undefined
            )
        ) {
            notes.push(
                _('La recomendación de Nitrógeno es híbrido específica para cada híbrido "{{ brand }}"', {
                    brand: this.props.brand.name,
                })
            );
        }

        return notes;
    }

    render() {
        return (
            <FertilizationWrapper
                {...this.props}
                multiplier='estimatedYield'
                text={_(
                    'Requerimiento nutricional total para lograr el rendimiento estimado de los híbridos seleccionados. Te recomendamos hacer análisis de suelo y restar los kg/ha de nutrientes presentes en el suelo (entre 0 y 60cm) a nuestra recomendación.'
                )}
                notes={this.getNotes()}
            />
        );
    }
}

@autobind
export class SoyFertilization extends Component {
    static label = Fertilization.label;
    static icon = Fertilization.icon;

    render() {
        return (
            <FertilizationWrapper
                {...this.props}
                multiplier='environment'
                text={_(
                    'El objetivo de la fertilización es satisfacer las necesidades nutricionales del cultivo que aumentan según el rendimiento.'
                )}
                notes={[
                    _(
                        'Recordá que fertilizar es aplicar nutrientes en forma balanceada y precisa para el desarrollo sustentable.'
                    ),
                ]}
            />
        );
    }
}
